/**
 * Patient Portal — Premium Redesign (Feb 2026)
 * -------------------------------------------------------------
 * Route: /patient-record/:token  (public, unguessable token as auth)
 * Feels like Apple Health / Notion / Stripe / Linear — not a file list.
 *
 * Layout
 *  1. Hero card (greeting, clinic identity, stat pills, one-tap actions)
 *  2. Health Summary strip (blood, allergies, meds, doctor, next visit…)
 *  3. Health Timeline (grouped by date)
 *  4. Tab nav (Overview / Treatments / Prescriptions / Invoices /
 *     Consents / Warranties / X-rays / Photos / Files)
 *  5. Category views with rich, glossy cards + action buttons
 *  6. In-browser PDF viewer modal + X-ray lightbox with controls
 *  7. Download Center + Security trust footer
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Activity, AlertTriangle, Award, Calendar, Camera, CheckCircle2,
  Clock, Contrast as ContrastIcon, Download, Eye, FileText,
  Fingerprint, Heart, IndianRupee, Info, Loader2, Lock, MapPin,
  Maximize2, MessageCircle, Minus, PenSquare, Phone, Pill,
  Plus, Printer, Receipt, RefreshCcw, Rotate3d, Share2,
  Shield, ShieldCheck, Sparkles, Stethoscope, Sun, Syringe,
  Tag, User, X, ZoomIn, ZoomOut,
} from "lucide-react";
import SEO from "@/components/SEO";
import { apiError } from "@/lib/apiError";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

/* -------------------------- utilities -------------------------- */
const fmtDate = (iso, opts = { day: "numeric", month: "short", year: "numeric" }) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", opts); }
  catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN",
      { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};
const fmtMonthYear = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { month: "long", year: "numeric" }); }
  catch { return iso; }
};
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const humanSize = (b) => {
  if (!b) return "";
  const kb = b / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
const greeting = () => {
  const h = new Date().getHours();
  if (h < 5)  return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

/* -------------------------- top page -------------------------- */
export default function PatientRecord() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  const [pdf, setPdf] = useState(null); // { url, title }
  const [lightbox, setLightbox] = useState(null); // { url, title, list, index }

  useEffect(() => {
    api.get(`/public/patient-portal/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e, "Could not load your record.")));
  }, [token]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const fileUrl = (fid) => `${BASE}/api/public/patient-portal/${token}/file/${fid}`;
  const rxPdfUrl = (rid) => `${BASE}/api/public/patient-portal/${token}/prescription/${rid}/pdf`;
  const invPdfUrl = (iid) => `${BASE}/api/public/patient-portal/${token}/invoice/${iid}/pdf`;
  const zipUrl = `${BASE}/api/public/patient-portal/${token}/download-all`;

  const openPdf = (url, title) => setPdf({ url, title });
  const openLightbox = (list, index) => setLightbox({ list, index });

  return (
    <div className="min-h-screen bg-[#F6F5F1] text-ink pb-24" data-testid="patient-portal-page">
      <SEO title={`${data.patient.name} · Records`} noindex path={`/patient-record/${token}`} />

      <BackgroundOrnaments />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 space-y-6">
        <HeroCard data={data} zipUrl={zipUrl} />
        <HealthSummary data={data} />
        <Timeline data={data} onOpenPdf={openPdf} rxPdfUrl={rxPdfUrl} invPdfUrl={invPdfUrl} />

        <TabNav tab={tab} setTab={setTab} counts={data.counts} />

        <div className="min-h-[280px]">
          {tab === "overview"      && <OverviewTab data={data} setTab={setTab} />}
          {tab === "treatments"    && <TreatmentsTab items={data.treatments} doctor={data.doctor} />}
          {tab === "prescriptions" && <PrescriptionsTab items={data.prescriptions} openPdf={openPdf} rxPdfUrl={rxPdfUrl} />}
          {tab === "invoices"      && <InvoicesTab items={data.invoices} openPdf={openPdf} invPdfUrl={invPdfUrl} />}
          {tab === "consents"      && <ConsentsTab items={data.consents} />}
          {tab === "warranties"    && <WarrantiesTab items={data.warranties} />}
          {tab === "xrays"         && <GalleryTab items={data.xrays} label="X-ray" fileUrl={fileUrl} onOpen={openLightbox} />}
          {tab === "photos"        && <GalleryTab items={data.photos} label="Photo" fileUrl={fileUrl} onOpen={openLightbox} />}
          {tab === "files"         && <FilesTab items={data.other_files} fileUrl={fileUrl} openPdf={openPdf} />}
        </div>

        <SecurityFooter data={data} />
      </div>

      {pdf && <PdfViewerModal {...pdf} onClose={() => setPdf(null)} />}
      {lightbox && (
        <ImageLightbox
          list={lightbox.list} startIndex={lightbox.index} fileUrl={fileUrl}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* -------------------------- backgrounds -------------------------- */
function BackgroundOrnaments() {
  return (
    <>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#1A362B]/8 to-transparent blur-3xl" />
        <div className="absolute -top-16 right-0 w-[440px] h-[440px] rounded-full bg-gradient-to-br from-[#D06A42]/10 to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[#B89C81]/8 to-transparent blur-3xl" />
      </div>
    </>
  );
}

/* -------------------------- 1. HERO -------------------------- */
function HeroCard({ data, zipUrl }) {
  const { patient, clinic, doctor, counts, next_appointment } = data;
  const firstName = (patient.name || "").split(" ")[0] || "there";

  const stats = [
    { label: "Treatments",   value: counts.treatments,   icon: Stethoscope },
    { label: "Invoices",     value: counts.invoices,     icon: Receipt },
    { label: "Prescriptions",value: counts.prescriptions,icon: Pill },
    { label: "Consents",     value: counts.consents,     icon: PenSquare },
    { label: "Warranties",   value: counts.warranties,   icon: Award },
    { label: "X-rays",       value: counts.xrays,        icon: Camera },
  ];

  const phone = clinic?.phone || "";
  const digits = phone.replace(/[^\d+]/g, "");
  const waNumber = digits.replace(/^\+/, "");
  const waMessage = encodeURIComponent(`Hello ${clinic?.name || "Doctor"}, this is ${patient.name}.`);
  const mapUrl = clinic?.google_maps_url
    || (clinic?.address ? `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}` : null);

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A362B] via-[#264A3B] to-[#12291F] text-linen shadow-[0_30px_80px_-30px_rgba(26,54,43,0.55)]"
             data-testid="portal-hero">
      {/* subtle grain */}
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/5" />
      <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5" />

      <div className="relative p-6 sm:p-10">
        {/* top row: clinic identity + logo */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ClinicLogo clinic={clinic} size="lg" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-linen/60">Secure health vault</div>
              <div className="font-display text-xl sm:text-2xl truncate">{clinic?.name || "Your clinic"}</div>
              {doctor?.name && (
                <div className="text-sm text-linen/70 mt-0.5 flex items-center gap-1.5">
                  <Stethoscope className="w-3.5 h-3.5" />
                  {doctor.name}{doctor.specialty ? ` · ${doctor.specialty}` : ""}
                </div>
              )}
            </div>
          </div>

          <div className="hidden sm:flex flex-col items-end text-xs text-linen/60">
            <span className="inline-flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> Encrypted portal
            </span>
            {patient.patient_since && (
              <span className="mt-2">
                Patient since <span className="text-linen">{fmtMonthYear(patient.patient_since)}</span>
              </span>
            )}
          </div>
        </div>

        {/* greeting */}
        <div className="mt-8 sm:mt-10">
          <div className="text-linen/60 text-sm">{greeting()},</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              data-testid="portal-greeting">
            {firstName} <span aria-hidden="true">👋</span>
          </h1>
          <p className="mt-3 max-w-lg text-linen/75 text-sm sm:text-base">
            Welcome to your secure dental health vault. Everything about your care lives here,
            organised and always available.
          </p>
        </div>

        {/* stats pills */}
        <div className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3" data-testid="portal-stats">
          {stats.map((s) => (
            <div key={s.label}
                 className="rounded-2xl bg-white/8 backdrop-blur-sm border border-white/10 px-3 py-3 sm:px-4 sm:py-4 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-linen/60 text-[10px] uppercase tracking-widest">
                <s.icon className="w-3 h-3" />
                <span className="truncate">{s.label}</span>
              </div>
              <div className="font-display text-2xl sm:text-3xl leading-none tabular-nums">{s.value}</div>
            </div>
          ))}
        </div>

        {/* action bar */}
        <div className="mt-8 flex flex-wrap gap-2 sm:gap-3" data-testid="portal-actions">
          {digits && (
            <a href={`tel:${digits}`} data-testid="btn-call-clinic"
               className="btn-hero btn-hero-primary"><Phone className="w-4 h-4" /> Call clinic</a>
          )}
          {waNumber && (
            <a href={`https://wa.me/${waNumber}?text=${waMessage}`} target="_blank" rel="noopener noreferrer"
               data-testid="btn-whatsapp-clinic" className="btn-hero btn-hero-ghost">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {mapUrl && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" data-testid="btn-directions"
               className="btn-hero btn-hero-ghost"><MapPin className="w-4 h-4" /> Directions</a>
          )}
          {clinic?.slug && (
            <a href={`/book/${clinic.slug}`} target="_blank" rel="noopener noreferrer" data-testid="btn-book-appointment"
               className="btn-hero btn-hero-ghost"><Calendar className="w-4 h-4" /> Book appointment</a>
          )}
          <a href={zipUrl} data-testid="btn-download-all" className="btn-hero btn-hero-accent">
            <Download className="w-4 h-4" /> Download all records
          </a>
        </div>

        {next_appointment && (
          <div className="mt-6 inline-flex flex-wrap items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-3 text-sm"
               data-testid="next-appt-pill">
            <Calendar className="w-4 h-4 text-[#F7D5B7]" />
            <span className="text-linen/80">Next visit</span>
            <span className="font-medium">{fmtDateTime(next_appointment.start)}</span>
            <span className="text-linen/60">·</span>
            <span className="text-linen/80">{next_appointment.procedure || "Follow-up"}</span>
          </div>
        )}
      </div>

      {/* embedded button styles (scoped) */}
      <style>{`
        .btn-hero {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.65rem 1.1rem; border-radius: 999px; font-size: 0.875rem;
          font-weight: 500; transition: transform .15s ease, background .2s ease;
          white-space: nowrap;
        }
        .btn-hero:active { transform: scale(.97); }
        .btn-hero-primary { background: #F5F3ED; color: #1A362B; }
        .btn-hero-primary:hover { background: #ffffff; }
        .btn-hero-ghost { background: rgba(255,255,255,.08); color: #F5F3ED; border: 1px solid rgba(255,255,255,.14); }
        .btn-hero-ghost:hover { background: rgba(255,255,255,.14); }
        .btn-hero-accent { background: #D06A42; color: #fff; }
        .btn-hero-accent:hover { background: #E17550; }
      `}</style>
    </section>
  );
}

function ClinicLogo({ clinic, size = "md" }) {
  const s = { sm: "w-9 h-9 text-sm", md: "w-12 h-12 text-base", lg: "w-16 h-16 text-lg" }[size];
  if (clinic?.logo_url) {
    return <img src={clinic.logo_url} alt={clinic.name}
                className={`${s} rounded-2xl object-cover bg-white/90 shadow-lg shadow-black/20`} />;
  }
  const letter = (clinic?.name || "V").trim()[0]?.toUpperCase() || "V";
  return (
    <div className={`${s} rounded-2xl grid place-items-center bg-white text-[#1A362B] font-display font-semibold shadow-lg shadow-black/20`}>
      {letter}
    </div>
  );
}

/* -------------------------- 2. HEALTH SUMMARY -------------------------- */
function HealthSummary({ data }) {
  const { patient, doctor, next_appointment, counts, flags } = data;
  const items = [
    { icon: Heart, label: "Blood group",      value: patient.blood_group || "—" },
    { icon: AlertTriangle, label: "Allergies", value: patient.allergies || "None reported", danger: !!patient.allergies },
    { icon: Activity, label: "Conditions",     value: (patient.special_conditions?.length ? patient.special_conditions.join(", ")
                                                     : patient.medical_history?.length
                                                       ? patient.medical_history.map((h) => h.condition).slice(0, 2).join(", ")
                                                       : "None reported") },
    { icon: Pill,    label: "Medications",     value: patient.current_medications?.length
                                                       ? patient.current_medications.join(", ")
                                                       : "None on record" },
    { icon: Stethoscope, label: "Treating doctor", value: doctor?.name || "Assigned by clinic" },
    { icon: Calendar,    label: "Next visit",  value: next_appointment ? fmtDateTime(next_appointment.start) : "None scheduled" },
    { icon: Clock,       label: "Pending treatment", value: counts.pending_treatments > 0 ? `${counts.pending_treatments} to complete` : "All up to date" },
    { icon: ShieldCheck, label: "Warranty",    value: flags.active_warranty ? "Active" : (counts.warranties ? "Expired" : "None"),
      good: flags.active_warranty },
  ];

  return (
    <section className="rounded-3xl bg-white/70 backdrop-blur-md border border-[#E7E1D3] p-5 sm:p-6 shadow-sm"
             data-testid="health-summary">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-4 h-4 text-[#B94A48]" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">Health summary</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {items.map((i) => (
          <div key={i.label} className="rounded-2xl bg-[#FBFAF6] border border-[#EFEADC] p-3 sm:p-4"
               data-testid={`hs-${i.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center mb-2 ${
              i.danger ? "bg-[#FCECEC] text-[#B94A48]"
              : i.good ? "bg-[#E8F0EB] text-[#2D6A4F]"
              : "bg-[#F0EBDD] text-[#1A362B]"}`}>
              <i.icon className="w-4 h-4" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">{i.label}</div>
            <div className="font-medium text-sm text-ink mt-0.5 line-clamp-2">{i.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------- 3. TIMELINE -------------------------- */
function Timeline({ data, onOpenPdf, rxPdfUrl, invPdfUrl }) {
  const events = useMemo(() => {
    const out = [];
    (data.treatments || []).forEach((t) => out.push({
      key: `tx-${t.id}`, date: t.date || t.created_at,
      icon: Stethoscope, tone: t.status === "completed" ? "success" : "info",
      title: `${t.status === "completed" ? "✓ " : ""}${t.procedure}${t.variant ? " · " + t.variant : ""}${t.tooth ? " · Tooth " + t.tooth : ""}`,
      subtitle: t.status === "completed" ? "Treatment completed" : t.status === "in_progress" ? "In progress" : "Planned",
    }));
    (data.prescriptions || []).forEach((r) => out.push({
      key: `rx-${r.id}`, date: r.date || r.created_at,
      icon: Pill, tone: "clay",
      title: `Prescription ${r.rx_number || ""}`,
      subtitle: (r.items?.length || 0) + " medicine" + ((r.items?.length || 0) === 1 ? "" : "s"),
      action: { label: "View PDF", onClick: () => onOpenPdf(rxPdfUrl(r.id), `Prescription ${r.rx_number || ""}`) },
    }));
    (data.invoices || []).forEach((i) => out.push({
      key: `inv-${i.id}`, date: i.created_at,
      icon: Receipt, tone: i.status === "paid" ? "success" : "warn",
      title: `Invoice ${i.invoice_number || i.id.slice(0, 6)}`,
      subtitle: `${INR(i.total)} · ${(i.status || "pending").replace("_", " ")}`,
      action: { label: "View PDF", onClick: () => onOpenPdf(invPdfUrl(i.id), `Invoice ${i.invoice_number || ""}`) },
    }));
    (data.warranties || []).forEach((w) => out.push({
      key: `w-${w.id}`, date: w.issue_date || w.created_at,
      icon: Award, tone: w.voided ? "warn" : "success",
      title: `Warranty ${w.procedure}`,
      subtitle: `Valid till ${fmtDate(w.valid_till)}`,
    }));
    (data.consents || []).forEach((c) => out.push({
      key: `c-${c.id}`, date: c.patient_signed_at || c.created_at,
      icon: PenSquare, tone: "info",
      title: `Consent · ${c.title}`,
      subtitle: c.patient_signed_at ? `Signed ${fmtDate(c.patient_signed_at)}` : (c.status || "Pending signature"),
    }));
    (data.instructions || []).forEach((i) => out.push({
      key: `ins-${i.id}`, date: i.created_at,
      icon: FileText, tone: "info",
      title: "Post-op instructions",
      subtitle: i.title,
    }));
    (data.xrays || []).forEach((f) => out.push({
      key: `x-${f.id}`, date: f.created_at,
      icon: Camera, tone: "info",
      title: "X-ray uploaded",
      subtitle: f.original_filename,
    }));
    (data.past_appointments || []).forEach((a) => out.push({
      key: `ap-${a.id}`, date: a.start,
      icon: Calendar, tone: a.status === "completed" ? "success" : "warn",
      title: `Visit · ${a.procedure || "Appointment"}`,
      subtitle: (a.status || "").replace("_", " "),
    }));
    return out
      .filter((e) => e.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data, onOpenPdf, rxPdfUrl, invPdfUrl]);

  const grouped = useMemo(() => {
    const g = {};
    events.forEach((e) => {
      const k = fmtDate(e.date);
      (g[k] ||= []).push(e);
    });
    return g;
  }, [events]);

  if (events.length === 0) {
    return (
      <section className="rounded-3xl bg-white/70 border border-[#E7E1D3] p-8 text-center" data-testid="timeline-empty">
        <Sparkles className="w-6 h-6 text-ink-soft mx-auto mb-2" />
        <div className="text-sm text-ink-mid">
          Your health timeline will appear here after your first visit.
        </div>
      </section>
    );
  }

  const toneMap = {
    success: "bg-[#E8F0EB] text-[#2D6A4F] border-[#B8D6C2]",
    warn:    "bg-[#FDF3ED] text-[#8A3F1E] border-[#F2D2BC]",
    info:    "bg-[#EEF1F5] text-[#28527A] border-[#CFDAE6]",
    clay:    "bg-[#FDF3ED] text-[#D06A42] border-[#F2D2BC]",
  };

  return (
    <section className="rounded-3xl bg-white/70 backdrop-blur-md border border-[#E7E1D3] p-5 sm:p-8 shadow-sm"
             data-testid="patient-timeline">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-ink" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">Health timeline</div>
        </div>
        <div className="text-xs text-ink-soft">{events.length} events</div>
      </div>

      <div className="relative pl-6 sm:pl-8">
        <div className="absolute top-0 bottom-0 left-2 sm:left-3 w-px bg-[#EFEADC]" />
        {Object.entries(grouped).map(([day, list]) => (
          <div key={day} className="mb-8 last:mb-0">
            <div className="relative -ml-6 sm:-ml-8 flex items-center gap-3 mb-3">
              <div className="w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-pine text-linen grid place-items-center shrink-0 shadow ring-4 ring-[#F6F5F1]">
                <div className="w-1.5 h-1.5 rounded-full bg-linen" />
              </div>
              <div className="font-medium text-sm text-ink">{day}</div>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {list.map((e) => (
                <div key={e.key} className={`rounded-2xl border px-4 py-3 flex items-start gap-3 ${toneMap[e.tone]}`}
                     data-testid={`timeline-${e.key}`}>
                  <e.icon className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{e.title}</div>
                    <div className="text-xs opacity-80 mt-0.5 truncate">{e.subtitle}</div>
                  </div>
                  {e.action && (
                    <button onClick={e.action.onClick}
                            className="text-xs font-medium underline decoration-current/40 hover:decoration-current shrink-0">
                      {e.action.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------- 4. TAB NAV -------------------------- */
const TABS = [
  { key: "overview",      label: "Overview",      icon: Sparkles },
  { key: "treatments",    label: "Treatments",    icon: Stethoscope },
  { key: "prescriptions", label: "Prescriptions", icon: Pill },
  { key: "invoices",      label: "Invoices",      icon: Receipt },
  { key: "consents",      label: "Consents",      icon: PenSquare },
  { key: "warranties",    label: "Warranties",    icon: Award },
  { key: "xrays",         label: "X-rays",        icon: Camera },
  { key: "photos",        label: "Photos",        icon: Camera },
  { key: "files",         label: "Files",         icon: FileText },
];

function TabNav({ tab, setTab, counts }) {
  return (
    <div className="sticky top-3 z-30" data-testid="portal-tabs">
      <div className="rounded-2xl bg-white/85 backdrop-blur-md border border-[#E7E1D3] shadow-sm px-2 py-2 overflow-x-auto">
        <div className="flex gap-1">
          {TABS.map((t) => {
            const c = counts[t.key];
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  active ? "bg-pine text-linen shadow" : "text-ink-mid hover:bg-[#F6F1E5]"}`}>
                <t.icon className="w-4 h-4" />
                {t.label}
                {typeof c === "number" && c > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-pine/10 text-ink-mid"}`}>{c}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------------- 5. TAB VIEWS -------------------------- */
function OverviewTab({ data, setTab }) {
  const { counts } = data;
  const cards = [
    { k: "treatments",    label: "Treatments",    icon: Stethoscope, tone: "pine" },
    { k: "prescriptions", label: "Prescriptions", icon: Pill,        tone: "clay" },
    { k: "invoices",      label: "Invoices",      icon: Receipt,     tone: "pine" },
    { k: "warranties",    label: "Warranties",    icon: Award,       tone: "clay" },
    { k: "consents",      label: "Consents",      icon: PenSquare,   tone: "pine" },
    { k: "xrays",         label: "X-rays",        icon: Camera,      tone: "pine" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="overview-tab">
      {cards.map((c) => (
        <button key={c.k}
                onClick={() => setTab(c.k)}
                data-testid={`overview-card-${c.k}`}
                className="text-left rounded-2xl bg-white/80 backdrop-blur border border-[#E7E1D3] p-5 hover:border-pine/30 hover:shadow-md transition-all group">
          <div className={`w-10 h-10 rounded-full grid place-items-center mb-3 ${
            c.tone === "clay" ? "bg-[#FDF3ED] text-[#D06A42]" : "bg-[#F0EBDD] text-ink"}`}>
            <c.icon className="w-5 h-5" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">{c.label}</div>
          <div className="mt-1 flex items-end gap-2">
            <div className="font-display text-3xl leading-none tabular-nums text-ink">{counts[c.k] || 0}</div>
            <div className="text-xs text-ink-soft mb-1 group-hover:text-ink transition-colors">Open →</div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ----- treatments ----- */
function TreatmentsTab({ items, doctor }) {
  if (!items.length) return <EmptyState icon={Stethoscope} title="No treatments yet" hint="Your treatment history will appear here." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="treatments-tab">
      {items.map((t) => (
        <article key={t.id} className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Treatment</div>
              <h3 className="font-display text-xl text-ink mt-1 leading-tight">
                {t.procedure}{t.variant ? ` · ${t.variant}` : ""}
              </h3>
            </div>
            <StatusPill status={t.status} />
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <FieldTile label="Doctor" value={doctor?.name || "—"} />
            <FieldTile label="Cost"   value={INR(t.cost)} />
            <FieldTile label="Tooth"  value={t.tooth || "—"} />
            <FieldTile label="Date"   value={fmtDate(t.date)} />
          </div>

          {t.notes && (
            <div className="mt-4 rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-3 text-sm text-ink/80">
              {t.notes}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

/* ----- prescriptions ----- */
function PrescriptionsTab({ items, openPdf, rxPdfUrl }) {
  if (!items.length) return <EmptyState icon={Pill} title="No prescriptions" hint="Digital prescriptions from your doctor will appear here." />;
  return (
    <div className="space-y-4" data-testid="prescriptions-tab">
      {items.map((r) => (
        <article key={r.id} className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Prescription · {r.rx_number}</div>
              <h3 className="font-display text-lg text-ink mt-1">{r.diagnosis || "Prescription"}</h3>
              <div className="text-xs text-ink-mid mt-1">By {r.doctor_name || "your doctor"} · {fmtDate(r.date || r.created_at)}</div>
            </div>
            <div className="flex flex-wrap gap-1.5 shrink-0">
              <ActionBtn icon={Eye} onClick={() => openPdf(rxPdfUrl(r.id), `Prescription ${r.rx_number}`)}>View</ActionBtn>
              <ActionBtn icon={Download} as="a" href={rxPdfUrl(r.id)} download>Download</ActionBtn>
            </div>
          </div>

          <div className="mt-4 divide-y divide-[#EFEADC]">
            {(r.items || []).map((m, idx) => (
              <div
                key={m.id || `${r.id}-${m.drug_name || "med"}-${idx}`}
                className="py-2.5 grid grid-cols-[auto_1fr_auto] gap-3 items-center"
              >
                <div className="w-8 h-8 rounded-full bg-[#FDF3ED] text-[#D06A42] grid place-items-center">
                  <Pill className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">
                    {m.drug_name}{m.brand ? ` · ${m.brand}` : ""}{m.strength ? ` · ${m.strength}` : ""}
                  </div>
                  <div className="text-xs text-ink-mid truncate">{m.regimen}</div>
                  {m.instructions && <div className="text-xs text-ink-soft italic mt-0.5 truncate">{m.instructions}</div>}
                </div>
                <div className="text-xs text-ink-soft whitespace-nowrap">{m.quantity || ""}</div>
              </div>
            ))}
          </div>

          {r.notes && (
            <div className="mt-3 text-xs text-ink-mid bg-[#FBFAF6] border border-[#EFEADC] rounded-xl px-3 py-2">
              <strong className="text-ink">Doctor's note:</strong> {r.notes}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

/* ----- invoices ----- */
function InvoicesTab({ items, openPdf, invPdfUrl }) {
  if (!items.length) return <EmptyState icon={Receipt} title="No invoices" hint="Your billing history will appear here." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="invoices-tab">
      {items.map((i) => (
        <article key={i.id} className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Invoice</div>
              <h3 className="font-display text-lg text-ink mt-1">{i.invoice_number || i.id.slice(0, 8)}</h3>
              <div className="text-xs text-ink-mid mt-1">{fmtDate(i.created_at)}</div>
            </div>
            <StatusPill status={i.status} />
          </div>

          <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#FBFAF6] to-[#F5F0E3] border border-[#EFEADC] p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">Amount</div>
            <div className="mt-1 font-display text-3xl text-ink flex items-center">
              <IndianRupee className="w-6 h-6" />{Number(i.total || 0).toLocaleString("en-IN")}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-ink-mid">
              {i.payment_method && <div>Paid via {i.payment_method}</div>}
              {i.paid_at && <div>on {fmtDate(i.paid_at)}</div>}
              {i.gst != null && <div>GST · {INR(i.gst)}</div>}
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <ActionBtn icon={Eye} onClick={() => openPdf(invPdfUrl(i.id), `Invoice ${i.invoice_number || ""}`)}>View</ActionBtn>
            <ActionBtn icon={Download} as="a" href={invPdfUrl(i.id)} download>Download</ActionBtn>
            <ActionBtn icon={Printer} onClick={() => openPdf(invPdfUrl(i.id), `Invoice ${i.invoice_number || ""}`)}>Print</ActionBtn>
          </div>
        </article>
      ))}
    </div>
  );
}

/* ----- consents ----- */
function ConsentsTab({ items }) {
  if (!items.length) return <EmptyState icon={PenSquare} title="No consent forms" hint="Signed consent forms will appear here." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="consents-tab">
      {items.map((c) => (
        <article key={c.id} className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">Consent</div>
              <h3 className="font-display text-lg text-ink mt-1 line-clamp-2">{c.title}</h3>
            </div>
            <StatusPill status={c.status} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <FieldTile label="Signed on"  value={fmtDate(c.patient_signed_at)} />
            <FieldTile label="Countersigned" value={fmtDate(c.doctor_signed_at)} />
            <FieldTile label="Device"     value={c.signed_ua ? c.signed_ua.split(" ")[0] : "—"} />
            <FieldTile label="IP"         value={c.signed_ip || "—"} mono />
          </div>
          <div className="mt-4 rounded-xl bg-[#F0EBDD]/60 border border-[#EFEADC] px-3 py-2 flex items-center gap-2 text-xs text-ink-mid">
            <Fingerprint className="w-3.5 h-3.5" /> Legally binding · audit trail preserved
          </div>
          <div className="mt-4 flex gap-2">
            <ActionBtn icon={Eye} as="a" href={`/consent/${c.public_token}`} target="_blank">View signed</ActionBtn>
            <ActionBtn icon={Printer} as="a" href={`/consent/${c.public_token}`} target="_blank">Print</ActionBtn>
          </div>
        </article>
      ))}
    </div>
  );
}

/* ----- warranties ----- */
function WarrantiesTab({ items }) {
  if (!items.length) return <EmptyState icon={Award} title="No warranties" hint="Treatment warranties will appear here." />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="warranties-tab">
      {items.map((w) => {
        const active = !w.voided && (w.valid_till || "") >= new Date().toISOString().slice(0, 10);
        return (
          <article key={w.id} className="relative overflow-hidden rounded-2xl border border-[#E7E1D3] p-5 shadow-sm"
                   style={{ background: "linear-gradient(135deg, #FFFDF7 0%, #F5F0E3 100%)" }}>
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#D06A42]/10 blur-2xl" />
            <div className="relative">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft">Warranty</div>
                  <h3 className="font-display text-xl text-ink mt-1">
                    {w.procedure}{w.variant ? ` · ${w.variant}` : ""}
                  </h3>
                </div>
                <StatusPill status={active ? "active" : (w.voided ? "voided" : "expired")} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <FieldTile label="Warranty #" value={w.warranty_number} mono />
                <FieldTile label="Duration"   value={w.years_valid ? `${w.years_valid} years` : "—"} />
                <FieldTile label="Issued"     value={fmtDate(w.issue_date)} />
                <FieldTile label="Expires"    value={fmtDate(w.valid_till)} />
              </div>
              {(w.coverage_notes || w.exclusions) && (
                <div className="mt-3 space-y-2 text-xs">
                  {w.coverage_notes && <div className="text-ink/80"><strong>Covers:</strong> {w.coverage_notes}</div>}
                  {w.exclusions && <div className="text-ink-mid"><strong>Excludes:</strong> {w.exclusions}</div>}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <ActionBtn icon={Eye} as="a" href={`/warranty/${w.public_token}`} target="_blank">View card</ActionBtn>
                <ActionBtn icon={Download} as="a" href={`/warranty/${w.public_token}`} target="_blank">Download</ActionBtn>
                {active && (
                  <ActionBtn icon={ShieldCheck} as="a" href={`/warranty/${w.public_token}`} target="_blank" tone="clay">
                    Claim warranty
                  </ActionBtn>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

/* ----- image gallery (X-rays / Photos) ----- */
function GalleryTab({ items, label, fileUrl, onOpen }) {
  if (!items.length) return <EmptyState icon={Camera} title={`No ${label.toLowerCase()}s yet`} hint={`Uploaded ${label.toLowerCase()}s will appear here as a gallery.`} />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid={`gallery-${label.toLowerCase()}`}>
      {items.map((f, idx) => (
        <button key={f.id} onClick={() => onOpen(items, idx)}
                className="group relative rounded-2xl overflow-hidden bg-white border border-[#E7E1D3] shadow-sm hover:shadow-lg transition-all"
                data-testid={`gallery-item-${f.id}`}>
          <div className="aspect-square bg-[#0B0F0D] grid place-items-center overflow-hidden">
            {f.content_type?.startsWith("image/") ? (
              <img src={fileUrl(f.id)} alt={f.original_filename}
                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            ) : (
              <Camera className="w-8 h-8 text-white/30" />
            )}
          </div>
          <div className="p-3 text-left">
            <div className="text-xs font-medium text-ink truncate">{f.original_filename}</div>
            <div className="text-[10px] text-ink-soft mt-0.5">{fmtDate(f.created_at)} · {humanSize(f.size)}</div>
          </div>
          <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 backdrop-blur grid place-items-center text-white opacity-0 group-hover:opacity-100 transition">
            <Maximize2 className="w-3.5 h-3.5" />
          </div>
        </button>
      ))}
    </div>
  );
}

/* ----- other files ----- */
function FilesTab({ items, fileUrl, openPdf }) {
  if (!items.length) return <EmptyState icon={FileText} title="No documents" hint="Additional documents shared by your clinic will appear here." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="files-tab">
      {items.map((f) => {
        const isPdf = (f.content_type || "").includes("pdf");
        return (
          <article key={f.id} className="rounded-2xl bg-white/85 border border-[#E7E1D3] p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#F0EBDD] text-ink grid place-items-center shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink truncate">{f.original_filename}</div>
              <div className="text-xs text-ink-mid">{fmtDate(f.created_at)} · {humanSize(f.size)}</div>
            </div>
            <div className="flex gap-1.5">
              {isPdf ? (
                <ActionBtn icon={Eye} onClick={() => openPdf(fileUrl(f.id), f.original_filename)}>View</ActionBtn>
              ) : (
                <ActionBtn icon={Eye} as="a" href={fileUrl(f.id)} target="_blank">Open</ActionBtn>
              )}
              <ActionBtn icon={Download} as="a" href={fileUrl(f.id)} download>Save</ActionBtn>
            </div>
          </article>
        );
      })}
    </div>
  );
}

/* -------------------------- 6. modals -------------------------- */
function PdfViewerModal({ url, title, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6"
         data-testid="pdf-viewer">
      <div className="w-full max-w-5xl h-[90vh] rounded-2xl bg-white overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EFEADC]">
          <div className="min-w-0 flex items-center gap-2">
            <FileText className="w-4 h-4 text-ink shrink-0" />
            <div className="font-medium text-ink truncate">{title}</div>
          </div>
          <div className="flex items-center gap-1">
            <ActionBtn icon={Download} as="a" href={url} download tone="ghost">Download</ActionBtn>
            <ActionBtn icon={Printer} as="a" href={url} target="_blank" tone="ghost">Print</ActionBtn>
            <ActionBtn icon={Share2} as="a" href={url} target="_blank" tone="ghost">Share</ActionBtn>
            <button onClick={onClose} data-testid="pdf-close"
                    className="w-9 h-9 rounded-full hover:bg-[#F0EBDD] grid place-items-center">
              <X className="w-4 h-4 text-ink" />
            </button>
          </div>
        </div>
        <iframe title={title} src={url} className="flex-1 w-full border-0" />
      </div>
    </div>
  );
}

function ImageLightbox({ list, startIndex, fileUrl, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [invert, setInvert] = useState(false);
  const current = list[index];

  const next = useCallback(() => setIndex((i) => Math.min(list.length - 1, i + 1)), [list.length]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const reset = () => { setZoom(1); setRot(0); setBrightness(100); setContrast(100); setInvert(false); };

  useEffect(() => { reset(); }, [index]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, next, prev]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="image-lightbox">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 text-white border-b border-white/10">
        <div className="min-w-0">
          <div className="font-medium truncate">{current.original_filename}</div>
          <div className="text-xs text-white/60">{fmtDate(current.created_at)} · {index + 1} of {list.length}</div>
        </div>
        <div className="flex items-center gap-1">
          <ActionBtn icon={Download} as="a" href={fileUrl(current.id)} download tone="dark">Download</ActionBtn>
          <button onClick={onClose} data-testid="lightbox-close"
                  className="w-9 h-9 rounded-full hover:bg-white/10 grid place-items-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* image */}
      <div className="flex-1 grid place-items-center overflow-hidden relative">
        {current.content_type?.startsWith("image/") ? (
          <img
            src={fileUrl(current.id)}
            alt={current.original_filename}
            className="max-w-none transition-transform duration-200 select-none"
            style={{
              transform: `scale(${zoom}) rotate(${rot}deg)`,
              filter: `brightness(${brightness}%) contrast(${contrast}%) ${invert ? "invert(1)" : ""}`,
              maxHeight: "80vh",
            }}
            draggable={false}
          />
        ) : (
          <div className="text-white/60 text-sm">Preview unavailable</div>
        )}
        {index > 0 && (
          <button onClick={prev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
            ←
          </button>
        )}
        {index < list.length - 1 && (
          <button onClick={next}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
            →
          </button>
        )}
      </div>

      {/* controls */}
      <div className="border-t border-white/10 px-4 py-3 flex flex-wrap gap-2 justify-center text-white">
        <IconBtn onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}><ZoomOut className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => setZoom((z) => Math.min(4, z + 0.2))}><ZoomIn className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={() => setRot((r) => r - 90)}><Rotate3d className="w-4 h-4" /></IconBtn>

        <Slider icon={Sun} value={brightness} min={30} max={200} onChange={setBrightness} label="Brightness" />
        <Slider icon={ContrastIcon} value={contrast} min={30} max={200} onChange={setContrast} label="Contrast" />

        <IconBtn onClick={() => setInvert((v) => !v)} active={invert} title="Invert">
          {invert ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          <span className="text-xs ml-1">Invert</span>
        </IconBtn>
        <IconBtn onClick={reset} title="Reset"><RefreshCcw className="w-4 h-4" /><span className="text-xs ml-1">Reset</span></IconBtn>
      </div>
    </div>
  );
}

function Slider({ icon: Icon, value, min, max, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 bg-white/8 hover:bg-white/12 rounded-full px-3 py-1.5">
      <Icon className="w-4 h-4" />
      <input type="range" min={min} max={max} value={value}
             onChange={(e) => onChange(Number(e.target.value))}
             className="w-20 accent-[#D06A42]" title={label} />
      <span className="text-xs tabular-nums w-9 text-right">{value}%</span>
    </label>
  );
}

function IconBtn({ children, active, ...rest }) {
  return (
    <button {...rest}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 ${
              active ? "bg-[#D06A42] text-white" : "bg-white/8 hover:bg-white/14 text-white"}`}>
      {children}
    </button>
  );
}

/* -------------------------- 7. SECURITY FOOTER -------------------------- */
function SecurityFooter({ data }) {
  const c = data.clinic || {};
  const badges = [
    { icon: ShieldCheck, label: "Secured by Viyora" },
    { icon: Lock,        label: "End-to-end encrypted" },
    { icon: Shield,      label: "HIPAA-ready" },
    { icon: Fingerprint, label: "Private link · unguessable" },
  ];
  return (
    <footer className="mt-10 rounded-3xl bg-white/70 backdrop-blur border border-[#E7E1D3] p-6" data-testid="portal-security">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {badges.map((b) => (
          <div key={b.label} className="rounded-2xl bg-[#F0EBDD]/50 border border-[#EFEADC] px-3 py-3 flex items-center gap-2">
            <b.icon className="w-4 h-4 text-ink" />
            <span className="text-xs text-ink/80">{b.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-6 text-sm text-ink-mid">
        <div>
          {c.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {c.phone}</div>}
          {c.address && <div className="flex items-start gap-2 mt-2"><MapPin className="w-4 h-4 mt-0.5" /> {c.address}</div>}
          {c.email && <div className="flex items-start gap-2 mt-2"><MessageCircle className="w-4 h-4 mt-0.5" /> {c.email}</div>}
        </div>
        <div className="sm:text-right text-xs text-ink-soft">
          Only you have this private link. Do not share it with anyone.<br />
          Portal viewed {data.patient.name}. Powered by <span className="font-display text-ink">Viyora</span>.
        </div>
      </div>
    </footer>
  );
}

/* -------------------------- shared bits -------------------------- */
function StatusPill({ status }) {
  const map = {
    paid: "bg-[#E8F0EB] text-[#2D6A4F]",
    completed: "bg-[#E8F0EB] text-[#2D6A4F]",
    active: "bg-[#E8F0EB] text-[#2D6A4F]",
    pending: "bg-[#FDF3ED] text-[#D06A42]",
    planned: "bg-[#FDF3ED] text-[#D06A42]",
    in_progress: "bg-[#EEF1F5] text-[#28527A]",
    draft: "bg-slate-100 text-slate-600",
    sent: "bg-blue-100 text-blue-700",
    overdue: "bg-[#FCECEC] text-[#B94A48]",
    voided: "bg-[#FCECEC] text-[#B94A48]",
    expired: "bg-slate-200 text-slate-600",
    patient_signed: "bg-[#FDF3ED] text-[#D06A42]",
  };
  const cls = map[status] || "bg-slate-100 text-slate-600";
  const label = (status || "—").replace(/_/g, " ");
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-medium capitalize ${cls}`}>{label}</span>;
}

function FieldTile({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`text-sm text-ink mt-0.5 font-medium truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, children, as = "button", tone = "default", ...rest }) {
  const El = as;
  const cls = {
    default: "bg-[#F0EBDD] hover:bg-[#E7DEC5] text-ink",
    clay:    "bg-[#D06A42] hover:bg-[#E17550] text-white",
    ghost:   "bg-transparent hover:bg-[#F0EBDD] text-ink",
    dark:    "bg-white/10 hover:bg-white/20 text-white",
  }[tone];
  return (
    <El {...rest}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors ${cls}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </El>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="rounded-3xl bg-white/70 backdrop-blur border border-[#E7E1D3] py-16 text-center" data-testid="empty-state">
      <div className="w-12 h-12 mx-auto rounded-full bg-[#F0EBDD] grid place-items-center mb-3">
        <Icon className="w-5 h-5 text-ink-mid" />
      </div>
      <div className="font-display text-lg text-ink">{title}</div>
      <div className="text-sm text-ink-mid mt-1 max-w-md mx-auto">{hint}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center text-ink-mid">
      <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /> Loading your record…</div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center p-6">
      <div className="max-w-md bg-white rounded-3xl border border-[#E7E1D3] p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-clay mx-auto mb-3" />
        <h1 className="font-display text-2xl text-ink mb-2">Record unavailable</h1>
        <p className="text-sm text-ink-mid">{message}</p>
        <p className="text-xs text-ink-soft mt-4">If you believe this is a mistake, contact your clinic — they can regenerate the link for you.</p>
      </div>
    </div>
  );
}
